import { Box, useTheme } from "@mui/material";
import React, { ReactNode } from "react";
import HeroWaveMask from "./HeroWaveMask";

export interface HeroSectionProps {
    /** Optional background image URL (e.g. cover photo). If omitted, a theme-based gradient is used. */
    imageUrl?: string | null;
    /** Minimum height of the hero (e.g. "80vh", "55vh", "400px"). Default "80vh". */
    minHeight?: string;
    /** Whether to show the wave mask at the bottom. Default true. */
    showWave?: boolean;
    /** Content overlain on the hero (e.g. title, tagline, bio). */
    children: ReactNode;
}

/**
 * Reusable hero section: full-width container with optional background image,
 * dark left overlay for text contrast, and optional wave mask at the bottom.
 */
const HeroSection: React.FC<HeroSectionProps> = ({
    imageUrl,
    minHeight = "80vh",
    showWave = true,
    children
}) => {
    const theme = useTheme();
    const pageBg = theme.palette.background.default;
    const fallbackGradient = `linear-gradient(135deg, ${theme.palette.primary.main}40 0%, ${theme.palette.secondary.main}40 100%)`;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight,
                display: "flex",
                alignItems: "center",
                backgroundImage: imageUrl ? `url(${imageUrl})` : fallbackGradient,
                backgroundSize: "cover",
                backgroundPosition: "center 40%",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 45%, transparent 70%)`,
                    pointerEvents: "none"
                }
            }}
        >
            {children}
            {showWave && <HeroWaveMask fill={pageBg} />}
        </Box>
    );
};

export default HeroSection;
